import { useState } from 'react';
import { CalendarDays, CheckCircle2 } from 'lucide-react';
import { patientApi } from '../api';
import type { MarketplaceProvider } from '../types';
import BookingModal from './BookingModal';

interface Props {
  provider: MarketplaceProvider;
  open: boolean;
  onClose: () => void;
}

export default function BookDoctorForm({ provider, open, onClose }: Props) {
  const [appointedAt, setAppointedAt] = useState('');
  const [consultationType, setConsultationType] = useState('CLINIC');
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!appointedAt) return;
    setSubmitting(true);
    try {
      const result = await patientApi.bookDoctor({
        providerId: provider.id,
        appointedAt,
        consultationType,
        reasonForVisit: reasonForVisit || undefined,
        paymentMode,
      });
      setSuccess(result);
    } catch {
      alert('Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <BookingModal title="Appointment Confirmed" open={open} onClose={onClose}>
        <div className="text-center py-4 space-y-3">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <h3 className="text-lg font-bold text-gray-900">Booking Confirmed!</h3>
          <p className="text-sm text-gray-600">
            Your appointment with <span className="font-semibold">{provider.name}</span> has been scheduled.
          </p>
          {success.tokenNumber && (
            <div className="bg-teal-50 rounded-xl p-3">
              <p className="text-xs text-teal-600 uppercase tracking-wider font-semibold">Token Number</p>
              <p className="text-2xl font-bold text-teal-700 mt-1">{success.tokenNumber}</p>
            </div>
          )}
          <p className="text-xs text-gray-400">
            {new Date(appointedAt).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' })}
          </p>
          <button onClick={onClose} className="btn-primary w-full mt-4">Done</button>
        </div>
      </BookingModal>
    );
  }

  return (
    <BookingModal title={`Book Appointment`} open={open} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
          <CalendarDays className="w-5 h-5 text-teal-600" />
          <div>
            <p className="text-sm font-semibold text-gray-900">{provider.name}</p>
            <p className="text-xs text-gray-500">{provider.specialties.join(', ')}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time *</label>
          <input
            type="datetime-local"
            className="input"
            value={appointedAt}
            onChange={e => setAppointedAt(e.target.value)}
            min={new Date().toISOString().slice(0, 16)}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Consultation Type</label>
          <select className="input" value={consultationType} onChange={e => setConsultationType(e.target.value)}>
            <option value="CLINIC">Clinic Visit</option>
            <option value="VIDEO">Video Consultation</option>
            <option value="HOME_VISIT">Home Visit</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Visit</label>
          <textarea
            className="input"
            rows={2}
            placeholder="Briefly describe your symptoms or reason..."
            value={reasonForVisit}
            onChange={e => setReasonForVisit(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
          <select className="input" value={paymentMode} onChange={e => setPaymentMode(e.target.value)}>
            <option value="CASH">Cash</option>
            <option value="INSURANCE">Insurance</option>
            <option value="BOTH">Cash + Insurance</option>
          </select>
        </div>

        <p className="text-[10px] text-gray-400 leading-relaxed">By confirming, you consent to sharing relevant medical details with this provider per NeoHealthCard policy. All access is audit logged.</p>

        <button type="submit" disabled={submitting || !appointedAt} className="btn-primary w-full">
          {submitting ? 'Booking...' : 'Confirm Appointment'}
        </button>
      </form>
    </BookingModal>
  );
}
